import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, BedDouble, ShieldCheck, FileText, Tag } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  admitted: "border-amber-200 bg-amber-50 text-amber-700",
  discharged: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const PAYER_OPTIONS = [
  { value: "cash",       label: "Cash / Self-pay" },
  { value: "insurance",  label: "Insurance" },
  { value: "panel",      label: "Panel (TPA)" },
  { value: "corporate",  label: "Corporate" },
  { value: "camp",       label: "Camp / Sponsored" },
  { value: "free",       label: "Free / Charity" },
];

// Build "HH:MM" for the user's current IST clock — default for new admissions.
const _initialTime = () => {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

// "YYYY-MM-DD" — today, IST. Used for display (date is locked to today).
const _todayLabel = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const EMPTY_FORM = {
  patient_id: "", patient_name: "",
  admission_type: "day_care",
  bed_no: "", ward: "", reason: "",
  consent_signed: false,
  payer_category: "cash",
  panel_company_id: "", panel_name: "",
  policy_no: "",
  tpa_name: "", corporate_name: "",
  admission_time: _initialTime(),
};

export default function IpdAdmissions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [patientQ, setPatientQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [panels, setPanels] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const { data } = await api.get("/ipd/admissions");
    setItems(data.items);
  };
  const loadPanels = async () => {
    try {
      const { data } = await api.get("/panel/companies");
      setPanels(data.items || []);
    } catch { /* roles like nurse may not have access; silent */ }
  };
  useEffect(() => { load(); loadPanels(); }, []);

  const search = async (q) => {
    setPatientQ(q);
    if (q.length < 2) { setPatients([]); return; }
    const { data } = await api.get("/patients", { params: { q, limit: 8 } });
    setPatients(data.items);
  };

  // When user picks a panel from the dropdown, denormalise its name
  const onPickPanel = (companyId) => {
    const comp = panels.find((c) => c.id === companyId);
    setForm((f) => ({
      ...f,
      panel_company_id: companyId,
      panel_name: comp ? comp.name : "",
      // Auto-fill TPA when picked company itself is a TPA
      tpa_name: comp && comp.company_type === "tpa" ? comp.name : f.tpa_name,
      corporate_name: comp && comp.company_type === "corporate" ? comp.name : f.corporate_name,
    }));
  };

  const showsPanelBlock = form.payer_category !== "cash" && form.payer_category !== "free";

  const save = async () => {
    if (!form.patient_id) { toast.error("Select patient"); return; }
    if (showsPanelBlock && !form.panel_company_id && !form.panel_name.trim()) {
      toast.error("Select a Panel / Insurance company or change payer to Cash.");
      return;
    }
    try {
      const { data: created } = await api.post("/ipd/admissions", {
        patient_id: form.patient_id,
        admission_type: form.admission_type,
        bed_no: form.bed_no,
        ward: form.ward,
        reason: form.reason,
        consent_signed: form.consent_signed,
        admission_time: form.admission_time,
        payer_category: form.payer_category,
        panel_company_id: showsPanelBlock ? form.panel_company_id : "",
        panel_name: showsPanelBlock ? form.panel_name : "",
        policy_no: showsPanelBlock ? form.policy_no : "",
        tpa_name: showsPanelBlock ? form.tpa_name : "",
        corporate_name: showsPanelBlock ? form.corporate_name : "",
      });
      toast.success("Patient admitted — opening sticker and consent…");
      setOpen(false);
      setForm({ ...EMPTY_FORM, admission_time: _initialTime() });
      setPatientQ("");
      load();
      // Auto-open the IPD sticker (for the patient file) followed by the
      // admission/general consent in separate tabs.
      if (created?.id) {
        window.open(`/print/ipd-sticker/${created.id}`, "_blank");
        window.open(`/print/ipd-consent/${created.id}`, "_blank");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">IPD / Day-care</div>
          <h1 className="font-heading text-3xl font-semibold">Admissions</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-700" data-testid="ipd-new-btn">
              <Plus size={14} className="mr-2" />New admission
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New admission</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              {/* Patient picker */}
              <div>
                <Label>Patient *</Label>
                {form.patient_id ? (
                  <div className="flex items-center justify-between rounded-md border border-border p-2">
                    {form.patient_name}
                    <Button size="sm" variant="outline" onClick={() => setForm({ ...form, patient_id: "", patient_name: "" })}>Change</Button>
                  </div>
                ) : (
                  <>
                    <Input value={patientQ} onChange={e => search(e.target.value)} placeholder="Search by name / UHID / mobile…" data-testid="ipd-patient-search" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border">
                        {patients.map(p => (
                          <button
                            key={p.id}
                            className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary"
                            onClick={() => { setForm(f => ({ ...f, patient_id: p.id, patient_name: p.name })); setPatients([]); setPatientQ(""); }}
                            data-testid={`ipd-patient-${p.uhid}`}
                          >
                            {p.name} <span className="text-xs text-ink-muted">{p.uhid}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Admission core fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.admission_type} onValueChange={v => setForm({ ...form, admission_type: v })}>
                    <SelectTrigger data-testid="ipd-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day_care">Day-care</SelectItem>
                      <SelectItem value="ipd">IPD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ward</Label>
                  <Input value={form.ward} onChange={e => setForm({ ...form, ward: e.target.value })} placeholder="General / Private / ICU" data-testid="ipd-ward-input" />
                </div>
                <div>
                  <Label>Bed / Slot</Label>
                  <Input value={form.bed_no} onChange={e => setForm({ ...form, bed_no: e.target.value })} placeholder="B-12" data-testid="ipd-bed-input" />
                </div>
              </div>

              <div>
                <Label>Reason for admission</Label>
                <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Phaco + IOL, both eyes" data-testid="ipd-reason-input" />
              </div>

              {/* Admission date (locked to today) + Time (editable HH:MM) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Admission date</Label>
                  <Input
                    type="date"
                    value={_todayLabel()}
                    readOnly
                    disabled
                    className="cursor-not-allowed bg-secondary/60"
                    data-testid="ipd-admit-date"
                  />
                  <div className="mt-1 text-[10px] text-ink-muted">Locked to today.</div>
                </div>
                <div>
                  <Label>Admission time</Label>
                  <Input
                    type="time"
                    value={form.admission_time}
                    onChange={e => setForm({ ...form, admission_time: e.target.value })}
                    data-testid="ipd-admit-time"
                  />
                  <div className="mt-1 text-[10px] text-ink-muted">24-hour IST. Defaults to now — adjust if back-entering.</div>
                </div>
              </div>

              {/* ===== Payer / Panel block ===== */}
              <div className="rounded-lg border border-border bg-secondary/40 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <ShieldCheck size={14} className="text-brand" />
                  <div className="text-xs font-semibold uppercase tracking-wider text-brand">Payer / Panel</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Payer category *</Label>
                    <Select value={form.payer_category} onValueChange={v => setForm({ ...form, payer_category: v })}>
                      <SelectTrigger data-testid="ipd-payer-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {showsPanelBlock && (
                    <div>
                      <Label>Panel / Insurance company *</Label>
                      <Select value={form.panel_company_id} onValueChange={onPickPanel}>
                        <SelectTrigger data-testid="ipd-panel-select"><SelectValue placeholder="Select company…" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {panels.length === 0 && <div className="px-3 py-2 text-xs text-ink-muted">No companies configured. Add one under Panel / TPA.</div>}
                          {panels.map(c => (
                            <SelectItem key={c.id} value={c.id} data-testid={`ipd-panel-option-${c.id}`}>
                              {c.name} <span className="text-[10px] text-ink-muted">· {c.company_type}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {showsPanelBlock && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label>Policy / Card no.</Label>
                      <Input value={form.policy_no} onChange={e => setForm({ ...form, policy_no: e.target.value })} placeholder="e.g. POL-12345678" data-testid="ipd-policy-no" />
                    </div>
                    <div>
                      <Label>TPA (if applicable)</Label>
                      <Input value={form.tpa_name} onChange={e => setForm({ ...form, tpa_name: e.target.value })} placeholder="e.g. Medi Assist TPA" data-testid="ipd-tpa-name" />
                    </div>
                    <div className="col-span-2">
                      <Label>Corporate / Employer</Label>
                      <Input value={form.corporate_name} onChange={e => setForm({ ...form, corporate_name: e.target.value })} placeholder="e.g. Wipro Limited" data-testid="ipd-corporate-name" />
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2">
                <Checkbox checked={form.consent_signed} onCheckedChange={v => setForm({ ...form, consent_signed: !!v })} data-testid="ipd-consent" />
                <span className="text-sm">Admission consent signed (NABH)</span>
              </label>

              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="ipd-save-btn">Admit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">IPD No.</th>
              <th className="px-3 py-2 text-left">Admitted</th>
              <th className="px-3 py-2 text-left">Patient</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Bed</th>
              <th className="px-3 py-2 text-left">Payer</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan="9" className="px-3 py-10 text-center text-ink-muted">No admissions yet.</td></tr>}
            {items.map(a => (
              <tr key={a.id} data-testid={`ipd-row-${a.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{a.ipd_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{(a.admitted_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2"><div className="font-medium">{a.patient_name}</div><div className="text-[11px] font-mono text-ink-muted">{a.patient_uhid}</div></td>
                <td className="px-3 py-2 capitalize">{a.admission_type.replace("_", " ")}</td>
                <td className="px-3 py-2">{a.bed_no || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="capitalize">{a.payer_category || "cash"}</div>
                  {a.panel_name && <div className="text-[10px] text-ink-muted">{a.panel_name}{a.policy_no ? ` · ${a.policy_no}` : ""}</div>}
                </td>
                <td className="px-3 py-2">{a.reason || "—"}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={STATUS[a.status] || ""}>{a.status}</Badge></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-2">
                    <a
                      href={`/print/ipd-sticker/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`ipd-sticker-btn-${a.id}`}
                    >
                      <Button size="sm" variant="outline" title="Print IPD sticker">
                        <Tag size={12} className="mr-1" />Sticker
                      </Button>
                    </a>
                    <a
                      href={`/print/ipd-consent/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`ipd-consent-btn-${a.id}`}
                    >
                      <Button size="sm" variant="outline" title="Reprint Admission / General consent">
                        <FileText size={12} className="mr-1" />Consent
                      </Button>
                    </a>
                    <Link to={`/app/ipd/${a.id}`}>
                      <Button size="sm" className="bg-brand hover:bg-brand-700"><BedDouble size={12} className="mr-1" />Open</Button>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
